import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, validatePassword } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Users, Building2, Loader2 } from "lucide-react";

const ROLES = [
  { value: "model", label: "Model", icon: User, desc: "Licence your own look" },
  { value: "agency", label: "Agency", icon: Users, desc: "Manage a roster of models" },
  { value: "client", label: "Client", icon: Building2, desc: "Book models for your brand" },
];

const GoogleLogo = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleSignIn = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [busy, setBusy] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", password: "", role: "model" });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  const doSignup = async (e) => {
    e.preventDefault();
    const pwError = validatePassword(signupForm.password);
    if (pwError) {
      toast.error(pwError);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", signupForm);
      login(data.token, data.user);
      toast.success("Account created — welcome to LOOKBOOK");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Signup failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-mist min-h-[calc(100vh-4rem)]">
      <div className="mx-auto flex max-w-md flex-col gap-6 px-4 py-14">
        <div className="text-center">
          <h1 className="font-display text-3xl tracking-[-0.01em]">Welcome to LOOKBOOK</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ethical image licensing for AI fashion.</p>
        </div>
        <Card className="rounded-2xl shadow-[0_10px_40px_rgba(30,26,22,0.08)]">
          <CardHeader className="pb-2">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2 rounded-xl">
                <TabsTrigger data-testid="auth-tab-login" value="login" className="rounded-lg">Log in</TabsTrigger>
                <TabsTrigger data-testid="auth-tab-signup" value="signup" className="rounded-lg">Sign up</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="mt-6">
                <form onSubmit={doLogin} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email">Email</Label>
                    <Input data-testid="auth-login-email-input" id="login-email" type="email" required value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} className="rounded-xl bg-card" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password">Password</Label>
                    <Input data-testid="auth-login-password-input" id="login-password" type="password" required value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="rounded-xl bg-card" />
                  </div>
                  <Button data-testid="auth-submit-button" type="submit" disabled={busy} className="w-full rounded-xl">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-6">
                <form onSubmit={doSignup} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>I am joining as</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {ROLES.map((r) => (
                        <button
                          key={r.value}
                          type="button"
                          data-testid={`auth-role-${r.value}`}
                          onClick={() => setSignupForm({ ...signupForm, role: r.value })}
                          className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            signupForm.role === r.value
                              ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]"
                              : "bg-card hover:bg-muted"
                          }`}
                        >
                          <r.icon className="h-5 w-5" />
                          <span className="text-xs font-semibold">{r.label}</span>
                          <span className="text-[10px] leading-tight text-muted-foreground">{r.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-name">{signupForm.role === "agency" ? "Agency name" : signupForm.role === "client" ? "Your name" : "Full name"}</Label>
                    <Input data-testid="auth-signup-name-input" id="signup-name" required value={signupForm.name}
                      onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} className="rounded-xl bg-card" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input data-testid="auth-signup-email-input" id="signup-email" type="email" required value={signupForm.email}
                      onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} className="rounded-xl bg-card" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-password">Password (min 6 chars, incl. a capital letter & a symbol)</Label>
                    <Input data-testid="auth-signup-password-input" id="signup-password" type="password" required minLength={6} value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} className="rounded-xl bg-card" />
                  </div>
                  <Button data-testid="auth-signup-submit-button" type="submit" disabled={busy} className="w-full rounded-xl">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">or</span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <Button
              type="button"
              data-testid="google-signin-button"
              variant="outline"
              onClick={startGoogleSignIn}
              className="mt-4 w-full rounded-xl border bg-card hover:bg-muted"
            >
              <GoogleLogo /> <span className="ml-2">Continue with Google</span>
            </Button>
            <p className="mt-3 text-center text-[11px] leading-snug text-muted-foreground">
              New here? Google sign-in creates your account instantly — you'll pick your role
              (Model, Agency or Client) right after.
            </p>
          </CardHeader>
          <CardContent />
        </Card>
      </div>
    </div>
  );
}
